// Video playback signal wiring (spec §2.2 video row / §7 task 2-3a).
//
// Bridges the Vimeo Player SDK's play/pause/timeupdate events to the meter's
// media-playing signal and to 25/50/75/90% milestone events. Milestone
// tracking is DOM/SDK-free so it can be unit tested without a real player.

export const MILESTONES = [25, 50, 75, 90] as const
export type Milestone = typeof MILESTONES[number]

// Tracks which milestones a playback session already crossed so a milestone
// fires only once even if the viewer seeks back past it and forward again.
export class MilestoneTracker {
  private readonly reached = new Set<Milestone>()

  // percent: 0..1 playback position (Vimeo's timeupdate payload shape).
  update(percent: number): Milestone[] {
    const pct = Math.round(percent * 100)
    const newly: Milestone[] = []
    for (const milestone of MILESTONES) {
      if (pct >= milestone && !this.reached.has(milestone)) {
        this.reached.add(milestone)
        newly.push(milestone)
      }
    }
    return newly
  }
}

export interface VimeoPlayerLike {
  on(event: string, callback: (payload: any) => void): void
  off(event: string, callback?: (payload: any) => void): void
}

export interface VideoSignalHandlers {
  onPlay(data?: VimeoTimeUpdate): void
  onPause(data?: VimeoTimeUpdate): void
  onMilestone(milestone: Milestone): void
  onTimeUpdate?(data: VimeoTimeUpdate): void
  onSeeked?(data: VimeoTimeUpdate): void
  onEnded?(data?: VimeoTimeUpdate): void
}

export interface VimeoTimeUpdate {
  seconds: number
  duration: number
  percent?: number
}

// Wires a Vimeo Player SDK instance to the given handlers. Returns a detach fn.
export function bindVimeoPlayerSignal(player: VimeoPlayerLike, handlers: VideoSignalHandlers): () => void {
  const tracker = new MilestoneTracker()

  const onPlay = (data?: VimeoTimeUpdate) => handlers.onPlay(data)
  const onPause = (data?: VimeoTimeUpdate) => handlers.onPause(data)
  const onEnded = (data?: VimeoTimeUpdate) => {
    if (handlers.onEnded) {
      handlers.onEnded(data)
    } else {
      handlers.onPause(data)
    }
  }
  const onTimeUpdate = (data: VimeoTimeUpdate) => {
    tracker.update(data.percent ?? 0).forEach((milestone) => handlers.onMilestone(milestone))
    handlers.onTimeUpdate?.(data)
  }
  const onSeeked = (data: VimeoTimeUpdate) => handlers.onSeeked?.(data)

  player.on('play', onPlay)
  player.on('pause', onPause)
  player.on('ended', onEnded)
  player.on('timeupdate', onTimeUpdate)
  player.on('seeked', onSeeked)

  return () => {
    player.off('play', onPlay)
    player.off('pause', onPause)
    player.off('ended', onEnded)
    player.off('timeupdate', onTimeUpdate)
    player.off('seeked', onSeeked)
  }
}
